import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import './home.css';
import './dark-mode.css';
import ZoomParallax from '../../components/ZoomParallax';
import FAQChatSection from '../../components/FAQChatSection';
import UserTypeSection from '../../components/UserTypeSection';
import TestimonialsSection from '../../components/TestimonialsSection';
import StatsSection from '../../components/StatsSection';
import CapturedMomentsSection from '../../components/CapturedMomentsSection';
import SEO from '../../components/SEO';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import api from '../../services/api';

// Composant pour l'effet de machine à écrire
const TypewriterEffect = ({ text }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const textRef = useRef(text); // Référence stable pour le texte
  
  // Réinitialiser l'effet si le texte change
  useEffect(() => {
    if (text !== textRef.current) {
      setDisplayedText('');
      setCurrentIndex(0);
      textRef.current = text;
    }
  }, [text]);
  
  useEffect(() => {
    if (currentIndex < textRef.current.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(prev => prev + textRef.current[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, 50); // Vitesse de frappe (en millisecondes)
      
      return () => clearTimeout(timeout);
    }
  }, [currentIndex]);
  
  return <>{displayedText}</>
};

// Enregistrer les plugins GSAP
gsap.registerPlugin(ScrollTrigger);

// Variantes d'animation pour les conteneurs
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Variantes d'animation pour les éléments
const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

const Home = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const isRTL = language === 'ar';
  
  // État pour contrôler la lecture de la vidéo
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoRef = useRef(null);
  const [newsData, setNewsData] = useState([]);
  const [loadingNews, setLoadingNews] = useState(true);
  const placeholderImage = '/image/default.jpg';
  
  // Fonction pour revenir en arrière
  const handleGoBack = () => {
    // navigate(-1);
  };

  // Référence pour suivre si les actualités ont déjà été chargées pour éviter les appels répétitifs
  const newsLoadedRef = useRef(false);
  
  // Récupérer les actualités depuis l'API avec mise en cache
  // DÉSACTIVÉ: L'API news n'est pas disponible actuellement
  useEffect(() => {
    // Initialiser avec un tableau vide et marquer comme chargé
    setNewsData([]);
    setLoadingNews(false);
    newsLoadedRef.current = true;
    
    /* COMMENTÉ: Appel API désactivé pour éviter les erreurs de connexion
    // Éviter les appels répétitifs si les actualités sont déjà chargées
    if (newsLoadedRef.current && newsData.length > 0) {
      return;
    }
    
    const fetchNews = async () => {
      // Vérifier si nous avons des actualités en cache
      const cachedNews = localStorage.getItem('dncNews');
      const cachedTime = localStorage.getItem('dncNewsTime');
      
      // Utiliser le cache si disponible et moins de 30 minutes
      if (cachedNews && cachedTime) {
        const now = new Date().getTime();
        const cacheAge = now - parseInt(cachedTime);
        
        // Cache valide pendant 30 minutes (1800000 ms)
        if (cacheAge < 1800000) {
          try {
            const parsedNews = JSON.parse(cachedNews);
            setNewsData(parsedNews);
            setLoadingNews(false);
            newsLoadedRef.current = true;
            return;
          } catch (e) {
            console.error('Erreur lors de la lecture du cache des actualités:', e);
            // Continuer avec l'appel API si le cache est corrompu
          }
        }
      }
      
      try {
        setLoadingNews(true);
        const response = await api.getNews();
        
        // Vérifier si la réponse contient des actualités
        if (!response.data || !response.data.news || !Array.isArray(response.data.news)) {
          setNewsData([]);
          newsLoadedRef.current = true;
          return;
        }
        
        const formattedNews = response.data.news
          .filter(item => item.isPublished === true) // Ne garder que les actualités publiées
          .map(item => {
            // S'assurer que la date est valide
            const dateObj = item.date ? new Date(item.date) : new Date(item.createdAt || Date.now());
            const validDate = isNaN(dateObj.getTime()) ? new Date() : dateObj;
            
            // Formater la date en chaîne de caractères pour éviter les problèmes de sérialisation
            const formattedDateStr = validDate.toLocaleDateString(language === 'ar' ? 'ar-SA' : 'fr-FR', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            });
            
            return {
              ...item,
              formattedDate: validDate,
              formattedDateStr: formattedDateStr // Stocker la date formatée comme chaîne
            };
          });
        
        // Mettre en cache les données
        localStorage.setItem('dncNews', JSON.stringify(formattedNews));
        localStorage.setItem('dncNewsTime', new Date().getTime().toString());
        
        setNewsData(formattedNews);
        newsLoadedRef.current = true;
      } catch (error) {
        console.error('Erreur lors de la récupération des actualités:', error);
        setNewsData([]);
      } finally {
        setLoadingNews(false);
      }
    };
    
    fetchNews();
    */
  }, []); // Supprimer language des dépendances pour éviter les appels répétitifs
  
  // Effet pour gérer le scroll vers les sections avec hash
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  // Effet pour le défilement fluide
  useEffect(() => {
    // Fonction pour rendre le défilement plus fluide
    const smoothScroll = () => {
      const currentScroll = window.pageYOffset;
      const smooth = gsap.utils.interpolate(currentScroll, window.pageYOffset, 0.1);
      window.scrollTo(0, smooth);
      requestAnimationFrame(smoothScroll);
    };

    // Nettoyage
    return () => {
      // cancelAnimationFrame si nécessaire
    };
  }, []);

  // Animation pour les sections
  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  // Section Guide
  const GuideSection = () => {
    const handleDownload = () => {
      const link = document.createElement('a');
      link.href = '/Guide.pdf';
      link.download = 'Guide-DNC.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    return (
      <section className="guide-section" id="guide">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="guide-section-header"
        >
          <h2 className="guide-section-title">
            {language === 'ar' ? 'دليلنا' : 'Notre Guide'}
          </h2>
          <p className="guide-section-subtitle">
            {language === 'ar' 
              ? 'اكتشف دليلنا الشامل للمنظومة الوطنية للفرصة الجديدة'
              : 'Découvrez notre guide complet du Dispositif National Nouvelle Chance'}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="guide-container-new"
        >
          <div className="guide-content-wrapper">
            <motion.div 
              className="guide-text-content"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h2 className="guide-main-title">
                {language === 'ar' ? 'فرص جديدة' : 'Nouvelles'}
              </h2>
              <h2 className="guide-main-title-highlight">
                {language === 'ar' ? 'فرص جديدة' : 'Chances'}
              </h2>
              <h3 className="guide-subtitle">
                {language === 'ar' 
                  ? 'في مواجهة التسرب المدرسي' 
                  : 'FACE AU DÉCROCHAGE SCOLAIRE'}
              </h3>
              <p className="guide-description-new">
                {language === 'ar' 
                  ? 'اكتشف كيف يمكن للمنظومة الوطنية للفرصة الجديدة أن تساعدك في بناء مستقبل أفضل والتغلب على التحديات التعليمية'
                  : 'Découvrez comment le Dispositif National Nouvelle Chance peut vous aider à construire un meilleur avenir et surmonter les défis éducatifs'}
              </p>
              <button 
                onClick={handleDownload}
                className="guide-download-btn-new"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {language === 'ar' ? 'تحميل الدليل' : 'Télécharger le guide'}
              </button>
            </motion.div>
            
            <motion.div 
              className="guide-image-content"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <img 
                src="/image/flou.jpeg" 
                alt={language === 'ar' ? 'فرص جديدة' : 'Nouvelles Chances'} 
                className="guide-image"
              />
            </motion.div>
          </div>
        </motion.div>
      </section>
    );
  };

  return (
    <div className="home-page">
      <SEO 
        url="/"
        type="website"
      />
      
      <main>
        {/* Section Zoom Parallax */}
        <section className="zoom-parallax-section">
          <ZoomParallax />
        </section>
        
        <section className="about-dnc-section">
          <div className="container">
            <motion.div 
              className="section-header"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="section-header-with-icon">
                <i className="fas fa-star-of-life section-icon"></i>
                <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>
                  {language === 'ar' 
                    ? 'عن المنظومة الوطنية للفرصة الجديدة' 
                    : 'À propos du Dispositif National Nouvelle Chance'}
                </h2>
              </div>
              <p style={{ fontSize: '1.2rem', color: '#666' }}>
                {language === 'ar' 
                  ? 'تعرف على المنظومة الوطنية للفرصة الجديدة وأهدافها' 
                  : 'Découvrez le Dispositif National Nouvelle Chance et ses objectifs'}
              </p>
            </motion.div>
            
            {/* Section vidéo "C'est quoi le DNC" */}
            <motion.div
              className="what-is-dnc-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              style={{ margin: '3rem 0', textAlign: 'center', padding: '2rem 0', backgroundColor: '#f8f9fa', borderRadius: '15px' }}
            >
              <h3 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '1.5rem', color: '#0f4c81' }}>
                {language === 'ar' 
                  ? 'ما هي المنظومة الوطنية للفرصة الجديدة؟' 
                  : 'C\'est quoi le Dispositif National Nouvelle Chance ?'}
              </h3>
              
              <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 1.5rem' }}>
                <div 
                  style={{ 
                    position: 'relative', 
                    borderRadius: '15px', 
                    overflow: 'hidden', 
                    boxShadow: '0 15px 35px rgba(15, 76, 129, 0.2)',
                    cursor: 'pointer'
                  }}
                >
                  {/* Overlay qui disparaît quand la vidéo est en lecture */}
                  {!isVideoPlaying && (
                    <motion.div 
                      style={{ 
                        position: 'absolute', 
                        top: 0, 
                        left: 0, 
                        width: '100%', 
                        height: '100%', 
                        backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.5))', 
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'center', 
                        alignItems: 'center',
                        zIndex: 2
                      }}
                      initial={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onClick={() => {
                        setIsVideoPlaying(true);
                        if (videoRef.current) {
                          videoRef.current.play();
                        }
                      }}
                    >
                      <motion.div 
                        style={{ 
                          width: '90px', 
                          height: '90px', 
                          backgroundColor: '#ffd166', 
                          borderRadius: '50%', 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center', 
                          marginBottom: '1rem',
                          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.25)'
                        }}
                        whileHover={{ scale: 1.1 }}
                      >
                        <i className="fas fa-play" style={{ color: '#0f4c81', fontSize: '2.2rem', marginLeft: '8px' }}></i>
                      </motion.div>
                      <p style={{ 
                        color: 'white', 
                        fontSize: '1.3rem', 
                        fontWeight: 600, 
                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.4)',
                        letterSpacing: '0.5px'
                      }}>
                        {language === 'ar' ? 'شاهد الفيديو' : 'Regarder la vidéo'}
                      </p>
                    </motion.div>
                  )}
                  
                  {/* Vidéo intégrée */}
                  <video 
                    ref={videoRef}
                    style={{ 
                      width: '100%', 
                      display: 'block', 
                      borderRadius: '15px',
                      backgroundColor: '#000',
                      aspectRatio: '16/9'
                    }}
                    poster="https://res.cloudinary.com/dozvy3xru/video/upload/so_0,q_auto,f_auto/mtnu4ii9d62qr8eqv6ar.jpg"
                    controls={isVideoPlaying}
                    onClick={() => {
                      if (!isVideoPlaying) {
                        setIsVideoPlaying(true);
                        videoRef.current.play();
                      }
                    }}
                    onPause={() => {
                      // Si l'utilisateur met en pause, on garde l'état "playing" pour conserver les contrôles
                      // mais on ne remet pas l'overlay
                    }}
                    onEnded={() => {
                      setIsVideoPlaying(false);
                      videoRef.current.currentTime = 0;
                    }}
                  >
                    <source src="https://res.cloudinary.com/dozvy3xru/video/upload/mtnu4ii9d62qr8eqv6ar" type="video/mp4" />
                    {language === 'ar' ? 'متصفحك لا يدعم تشغيل الفيديو' : 'Votre navigateur ne supporte pas la lecture vidéo'}
                  </video>
                </div>
                
                <p style={{ 
                  fontSize: '1.1rem', 
                  lineHeight: '1.7', 
                  marginTop: '1.5rem', 
                  textAlign: 'center',
                  color: '#444',
                  maxWidth: '90%',
                  margin: '1.5rem auto 0'
                }}>
                  {language === 'ar'
                    ? 'اكتشف المزيد حول المنظومة الوطنية للفرصة الجديدة وكيف يمكنها مساعدتك في بناء مستقبل أفضل. انقر على الفيديو لمشاهدته كاملاً.'
                    : 'Découvrez davantage sur le Dispositif National Nouvelle Chance et comment il peut vous aider à construire un meilleur avenir. Cliquez sur la vidéo pour la regarder en entier.'}
                </p>
              </div>
            </motion.div>
            
            <div className="about-dnc-content">
            <div className="about-dnc-text">
                                     <motion.h3
                                       initial={{ opacity: 0 }}
                                       whileInView={{ opacity: 1 }}
                                       transition={{ duration: 0.8 }}
                                     >
                                       <i className="fas fa-lightbulb title-icon"></i>
                                       {language === 'ar' 
                                         ? 'مشروع تجريبي في سوسة والقيروان لدعم الشباب غير المنخرط وتعزيز قابلية التشغيل'
                                         : 'Un projet pilote à Sousse et à Kairouan pour soutenir les jeunes sans diplômes et sans qualification et renforcer leur employabilité'}
                                     </motion.h3>
                                     
                                     <div className="typing-animation-container">
                                       <motion.div
                                         className="typing-text"
                                       >
                                          {language === 'ar' 
                                            ? <p>{"المنظومة الوطنية للفرصة الجديدة هي مشروع تجريبي في سوسة والقيروان يهدف إلى تعزيز فرص التشغيل وتمكين الشباب التونسي غير المنخرط في التعليم أو التدريب أو العمل (NEET) من خلال المرافقة والتوجيه والفرص العملية."}</p>
                                            : <p>{"Le Dispositif National Nouvelle Chance est un projet pilote à Sousse et à Kairouan visant à renforcer l'employabilité et à autonomiser les jeunes tunisiens NEET (ni en études, ni en emploi, ni en formation) par l'accompagnement, le mentorat et des opportunités pratiques."}</p>}
                                       </motion.div>
                                     </div>
                                     
                                     <motion.div
                                       className="about-dnc-features"
                                       initial={{ opacity: 0, y: 20 }}
                                       whileInView={{ opacity: 1, y: 0 }}
                                       transition={{ duration: 0.8, delay: 0.5 }}
                                     >
                                       <div className="feature">
                                         <div className="feature-icon">
                                           <i className="fas fa-graduation-cap"></i>
                                         </div>
                                         <div className="feature-text">
                                           <h4>{language === 'ar' ? 'بيداغوجيا متخصصة للشباب غير المنخرط' : 'Pédagogie spécialisée pour le public NEET (ni en études, ni en emploi, ni en formation)'}</h4>
                                           <p>{language === 'ar' 
                                             ? 'منهجية تعليمية مكيفة للشباب غير المنخرط مصممة لتطوير قابلية التشغيل'
                                             : 'Pédagogie adaptée pour le public NEET (ni en études, ni en emploi, ni en formation) conçue pour développer l\'employabilité'}
                                           </p>
                                         </div>
                                       </div>
                                       
                                       <div className="feature">
                                         <div className="feature-icon">
                                           <i className="fas fa-users"></i>
                                         </div>
                                         <div className="feature-text">
                                           <h4>{language === 'ar' ? 'مرافقة شخصية' : 'Accompagnement personnalisé'}</h4>
                                           <p>{language === 'ar' 
                                             ? 'مرافقة فردية لكل مشارك لتحقيق أهدافه'
                                             : 'Accompagnement individuel pour chaque participant afin d\'atteindre ses objectifs'}
                                           </p>
                                         </div>
                                       </div>
                                       
                                       <div className="feature">
                                         <div className="feature-icon">
                                           <i className="fas fa-briefcase"></i>
                                         </div>
                                         <div className="feature-text">
                                           <h4>{language === 'ar' ? 'فرص عملية' : 'Opportunités pratiques'}</h4>
                                           <p>{language === 'ar' 
                                             ? 'تدريبات عملية وفرص مهنية مع شركائنا'
                                             : 'Stages pratiques et opportunités professionnelles avec nos partenaires'}
                                           </p>
                                         </div>
                                       </div>
                                     </motion.div>
                                   </div>
      
              <motion.div 
                className="about-dnc-image"
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <img src={language === 'fr' ? '/image/logofr.png' : '/image/logoar.png'}  alt="Programme DNC" />
              </motion.div>
            </div>
            
            <div className="learn-more-container">
              <motion.a 
                href="/about" 
                className="btn-primary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {language === 'ar' ? 'معرفة المزيد' : 'En savoir plus'}
              </motion.a>
            </div>
          </div>
        </section>
                
        <StatsSection />
        
        <UserTypeSection />
        
        <GuideSection />
        
        <CapturedMomentsSection />
        
        <FAQChatSection />

        <TestimonialsSection />

        <section className="cta-section">
          <div className="cta-video-background">
            <video
              autoPlay
              muted
              loop
              playsInline
              className="cta-video"
            >
              <source src="/video/cta.mp4" type="video/mp4" />
            </video>
            <div className="cta-overlay"></div>
          </div>
          <motion.div 
            className="container cta-content"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="cta-title">{t('pedagogy.cta.title')}</h2>
            <p className="cta-text">{t('pedagogy.cta.text')}</p>
            <Link to="/registration" className="cta-button">{t('pedagogy.cta.button')}</Link>
          </motion.div>
        </section>
        
       
      </main>
      
    </div>
  );
};

export default Home;